import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { PageHead } from '../Layout';
import type {
  ArticleFormat,
  LibraryItem,
  LibraryKind,
  LibraryOrigin,
  MediaKind,
  StageStatus,
} from '../../shared/types';
import { formatDateTime } from '../format';
import './Library.css';

/**
 * Unified Library projection over ingested media and published outputs.
 * Media detail/edit actions continue through the focused /api/media routes.
 */

/** Full editable media detail; the unified Library list uses shared LibraryItem. */
interface MediaItem {
  id: number;
  kind: MediaKind;
  tgFileId: string | null;
  tgFileUniqueId: string | null;
  tgChatId: number | null;
  tgMessageId: number | null;
  sourceUrl: string | null;
  canonicalUrl: string | null;
  contentHash: string | null;
  origin: LibraryOrigin;
  storagePath: string | null;
  mime: string | null;
  sizeBytes: number | null;
  durationSeconds: number | null;
  title: string | null;
  author: string | null;
  fetchStatus: StageStatus;
  transcribeStatus: StageStatus;
  describeStatus: StageStatus;
  extractStatus: StageStatus;
  embedStatus: StageStatus;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  transcript: string | null;
  aiDescription: string | null;
  extractedText: string | null;
}

type LibrarySearchMode = 'search' | 'ask';

interface LibrarySearchHit {
  sourceType: string;
  sourceId: number;
  text: string;
  score: number;
  author?: string;
  sentAt?: string;
  title?: string;
  mediaKind?: MediaKind;
  publicationFormat?: ArticleFormat;
}

interface LibrarySearchResult {
  query: string;
  mode: LibrarySearchMode;
  answer: string | null;
  items: LibrarySearchHit[];
}

type CardPreviewStatus = 'loading' | 'pending' | 'done' | 'missing' | 'failed';

interface CardPreviewState {
  status: CardPreviewStatus;
  url?: string;
}

export type LibraryPreviewIssue = 'storage_quota' | 'unavailable';

export function classifyLibraryPreviewError(error: unknown): LibraryPreviewIssue {
  const detail = error instanceof ApiError && error.data && typeof error.data === 'object' && 'error' in error.data
    ? String((error.data as { error: unknown }).error)
    : error instanceof Error
      ? error.message
      : String(error);
  return /exceed_egress_quota/i.test(detail) ? 'storage_quota' : 'unavailable';
}

/** Native images and article covers resolve first; stable ordering is retained within each group. */
export function prioritizeLibraryPreviews<T extends Pick<LibraryItem, 'preview'>>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => Number(b.preview.status === 'done') - Number(a.preview.status === 'done'));
}

/** A worker queue lets later cards progress even while one request is slow. */
export async function runLibraryPreviewQueue<T>(
  items: readonly T[],
  resolve: (item: T) => Promise<'done' | 'retry' | 'stop'>,
  concurrency = 6,
): Promise<{ retry: T[]; stopped: boolean }> {
  let cursor = 0;
  let stopped = false;
  const retry: T[] = [];
  const worker = async () => {
    while (!stopped && cursor < items.length) {
      const item = items[cursor++];
      if (item === undefined) continue;
      const result = await resolve(item);
      if (result === 'retry') retry.push(item);
      if (result === 'stop') stopped = true;
    }
  };
  await Promise.all(Array.from({ length: Math.min(Math.max(1, concurrency), items.length) }, worker));
  return { retry, stopped };
}

const TABS: { label: string; kind: LibraryKind | '' }[] = [
  { label: 'All', kind: '' },
  { label: 'Images', kind: 'image' },
  { label: 'Voice', kind: 'voice' },
  { label: 'Video', kind: 'video' },
  { label: 'PDFs', kind: 'pdf' },
  { label: 'Links', kind: 'link' },
  { label: 'Articles', kind: 'article' },
  { label: 'Tweets', kind: 'tweet' },
];

const KIND_GLYPH: Record<LibraryKind, string> = {
  image: 'IMG',
  voice: 'VOI',
  audio: 'AUD',
  video: 'VID',
  pdf: 'PDF',
  doc: 'DOC',
  link: 'LNK',
  article: 'ART',
  tweet: 'TWT',
  quote_tweet: 'QTE',
  reply: 'RPL',
};

const STAGES: { key: keyof NonNullable<LibraryItem['pipeline']>; letter: string; label: string }[] = [
  { key: 'fetch', letter: 'F', label: 'Fetch' },
  { key: 'transcribe', letter: 'T', label: 'Transcribe' },
  { key: 'describe', letter: 'D', label: 'Describe' },
  { key: 'extract', letter: 'E', label: 'Extract' },
  { key: 'embed', letter: 'Em', label: 'Embed' },
];

function hasFailedStage(item: LibraryItem): boolean {
  return Boolean(item.pipeline && STAGES.some((s) => item.pipeline?.[s.key] === 'failed'));
}

function bytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function duration(s: number | null): string {
  if (s === null) return '—';
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}:${String(rem).padStart(2, '0')}`;
}

function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
}

function displayTitle(item: LibraryItem): string {
  if (item.title?.trim()) return item.title.trim();
  if (item.storagePath) return basename(item.storagePath);
  if (item.body?.trim()) {
    const text = item.body.trim().replace(/\s+/g, ' ');
    return text.length > 88 ? `${text.slice(0, 87).trimEnd()}…` : text;
  }
  if (item.sourceUrl) return item.sourceUrl;
  return `${item.objectType} #${item.sourceId}`;
}

function originLabel(origin: LibraryOrigin): string {
  if (origin === 'collective_original') return 'Collective original';
  if (origin === 'agent_generated') return 'Agent generated';
  return 'Third-party source';
}

function searchHitLabel(hit: LibrarySearchHit): string {
  const kind = hit.sourceType === 'publication'
    ? (hit.publicationFormat ?? 'publication').replaceAll('_', ' ')
    : hit.sourceType.replaceAll('_', ' ');
  const title = hit.title
    ?? (hit.sourceType === 'message'
      ? `message #${hit.sourceId}`
      : hit.sourceType === 'publication'
        ? `publication #${hit.sourceId}`
        : `media #${hit.sourceId}`);
  return `${title} · ${kind}`;
}

/** One small labeled square per pipeline stage. */
function StageStrip({ item }: { item: LibraryItem }) {
  if (!item.pipeline) return null;
  return (
    <div className="stage-strip">
      {STAGES.map((s) => {
        const status = item.pipeline![s.key] as StageStatus;
        return (
          <span className="stage-dot-wrap" key={s.key} title={`${s.label}: ${status}`}>
            <span className="stage-letter">{s.letter}</span>
            <span className={`stage-dot ${status}`} />
          </span>
        );
      })}
    </div>
  );
}

function PipelineStatus({ item }: { item: LibraryItem }) {
  if (!item.pipeline) return null;
  const statuses = STAGES.map((stage) => item.pipeline![stage.key] as StageStatus);
  const label = statuses.includes('failed')
    ? 'Needs attention'
    : statuses.some((status) => status === 'pending' || status === 'processing')
      ? 'Processing'
      : 'Processed';
  return <span className={`library-pipeline-status ${label === 'Processed' ? 'ready' : label === 'Needs attention' ? 'attention' : 'processing'}`}>{label}</span>;
}

interface UploadRow {
  name: string;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

type EditableField = 'title' | 'author' | 'transcript' | 'aiDescription' | 'extractedText';
type MediaPatch = Partial<Pick<MediaItem, EditableField>>;

type ContentField = 'transcript' | 'aiDescription' | 'extractedText';
const CONTENT_FIELDS: { label: string; field: ContentField }[] = [
  { label: 'Transcript', field: 'transcript' },
  { label: 'AI description', field: 'aiDescription' },
  { label: 'Extracted text', field: 'extractedText' },
];

function nullableText(value: string): string | null {
  return value.trim() || null;
}

export default function Library() {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [tab, setTab] = useState<LibraryKind | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cardPreviews, setCardPreviews] = useState<Record<string, CardPreviewState>>({});
  const [previewIssue, setPreviewIssue] = useState<LibraryPreviewIssue | null>(null);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MediaItem | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftAuthor, setDraftAuthor] = useState('');
  const [draftContent, setDraftContent] = useState<Record<ContentField, string>>({
    transcript: '',
    aiDescription: '',
    extractedText: '',
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<{ ideas: string[] } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<LibrarySearchResult | null>(null);
  const [searchBusy, setSearchBusy] = useState<LibrarySearchMode | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pendingSearchMediaId, setPendingSearchMediaId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPatchRef = useRef<MediaPatch>({});
  const previewTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const load = useCallback(async () => {
    try {
      const q = tab ? `?kind=${tab}&limit=200` : '?limit=200';
      const res = await api.get<{ items: LibraryItem[] }>(`/library${q}`);
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    setSelectedId(null);
    void load();
  }, [load]);

  // Resolve existing representative previews without letting one pending item
  // hold up later cards. Local mode never creates or downloads new artwork.
  useEffect(() => {
    const pending = prioritizeLibraryPreviews(
      items.filter((item) => cardPreviews[item.key]?.status !== 'done'),
    );
    if (pending.length === 0) return;
    let cancelled = false;

    setCardPreviews((previous) => {
      const next = { ...previous };
      for (const item of pending) next[item.key] = { status: 'loading' };
      return next;
    });

    const setPreview = (key: string, state: CardPreviewState) => {
      if (!cancelled) setCardPreviews((previous) => ({ ...previous, [key]: state }));
    };

    const terminal = new Set<string>();
    let quotaBlocked = false;

    const resolvePreview = async (item: LibraryItem): Promise<'done' | 'retry' | 'stop'> => {
      if (cancelled || quotaBlocked) return 'stop';
      try {
        const result = await api.post<{ status: CardPreviewStatus; url?: string }>(
          `/library/${item.objectType}/${item.sourceId}/preview`,
        );
        if (result.url) {
          terminal.add(item.key);
          setPreview(item.key, { status: 'done', url: result.url });
          return 'done';
        }
        if (result.status === 'pending') {
          setPreview(item.key, { status: 'pending' });
          return 'retry';
        }
        terminal.add(item.key);
        setPreview(item.key, { status: result.status === 'missing' ? 'missing' : 'failed' });
        return 'done';
      } catch (previewError) {
        const issue = classifyLibraryPreviewError(previewError);
        if (issue === 'storage_quota') {
          quotaBlocked = true;
          if (!cancelled) setPreviewIssue(issue);
          return 'stop';
        }
        terminal.add(item.key);
        setPreview(item.key, { status: 'failed' });
        return 'done';
      }
    };

    const deadline = Date.now() + 5 * 60_000;
    const resolveRound = async (round: LibraryItem[]): Promise<void> => {
      const outcome = await runLibraryPreviewQueue(round, resolvePreview);
      if (cancelled) return;
      if (outcome.stopped || quotaBlocked) {
        setCardPreviews((previous) => {
          const next = { ...previous };
          for (const item of pending) {
            if (!terminal.has(item.key)) next[item.key] = { status: 'failed' };
          }
          return next;
        });
        return;
      }
      if (outcome.retry.length > 0 && Date.now() < deadline) {
        previewTimersRef.current.push(setTimeout(() => void resolveRound(outcome.retry), 3000));
      } else {
        for (const item of outcome.retry) setPreview(item.key, { status: 'failed' });
      }
    };
    void resolveRound(pending);
    return () => {
      cancelled = true;
      for (const timer of previewTimersRef.current) clearTimeout(timer);
      previewTimersRef.current = [];
    };
    // Preview state updates must not restart the queue they belong to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, previewAttempt]);

  function retryPreviews() {
    setPreviewIssue(null);
    setPreviewAttempt((attempt) => attempt + 1);
  }

  const selected = items.find((it) => it.key === selectedId) ?? null;

  useEffect(() => {
    if (pendingSearchMediaId === null || loading) return;
    const key = `media:${pendingSearchMediaId}`;
    setSelectedId(items.some((item) => item.key === key) ? key : null);
    setPendingSearchMediaId(null);
  }, [items, loading, pendingSearchMediaId]);

  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    pendingPatchRef.current = {};
    setDraftTitle(selected?.title ?? '');
    setDraftAuthor(selected?.author ?? '');
    setDraftContent({ transcript: '', aiDescription: '', extractedText: '' });
    setDeleteBlocked(null);
  }, [selected?.key]);

  // List rows omit the large text columns, so selecting a card fetches the
  // full asset. The cancelled flag keeps a quick reselect from applying an
  // older response; the content drafts are seeded together with the detail.
  useEffect(() => {
    setDetail(null);
    if (!selected || selected.objectType !== 'media') return;
    let cancelled = false;
    api
      .get<MediaItem>(`/media/${selected.sourceId}`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setDraftContent({
          transcript: d.transcript ?? '',
          aiDescription: d.aiDescription ?? '',
          extractedText: d.extractedText ?? '',
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [selected, selectedId]);

  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    },
    [],
  );

  // Auto-scroll the detail panel into view when it opens or moves — 'nearest'
  // only scrolls if the panel isn't already fully visible.
  useEffect(() => {
    if (selectedId === null) return;
    panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // Full-page drag-and-drop: dragenter/dragleave nest per element, so a depth
  // counter (not a boolean) is what keeps the overlay from flickering as the
  // pointer crosses child elements while dragging over the page.
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Boolean(e.dataTransfer?.types.includes('Files'));

    function onDragEnter(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragActive(true);
    }
    function onDragOver(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
    }
    function onDragLeave(e: DragEvent) {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    }
    function onDrop(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) void uploadFiles(Array.from(files));
    }

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  /** Uploads sequentially so the progress list reflects real completion order. */
  async function uploadFiles(files: File[]) {
    setUploads(files.map((f) => ({ name: f.name, status: 'uploading' })));
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;
      try {
        const form = new FormData();
        form.append('file', file);
        await api.upload(`/media/upload`, form);
        setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, status: 'done' } : u)));
      } catch (err) {
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i
              ? { ...u, status: 'error', error: err instanceof Error ? err.message : String(err) }
              : u,
          ),
        );
      }
    }
    await load();
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) void uploadFiles(Array.from(files));
    e.target.value = '';
  }

  function queueAutoSave(field: EditableField, value: string) {
    if (!selected || selected.objectType !== 'media') return;

    pendingPatchRef.current = { ...pendingPatchRef.current, [field]: nullableText(value) };
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    const assetId = selected.sourceId;
    autoSaveTimerRef.current = setTimeout(() => {
      const patch = pendingPatchRef.current;
      pendingPatchRef.current = {};
      autoSaveTimerRef.current = null;

      void (async () => {
        setBusy('save');
        setError(null);
        try {
          const updated = await api.patch<MediaItem>(`/media/${assetId}`, patch);
          setItems((prev) =>
            prev.map((it) =>
              it.key === `media:${updated.id}`
                ? { ...it, title: updated.title, author: updated.author, origin: updated.origin }
                : it,
            ),
          );
          setDetail((prev) => (prev && prev.id === updated.id ? updated : prev));
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(null);
        }
      })();
    }, 700);
  }

  async function saveOrigin(origin: LibraryOrigin) {
    if (!selected || selected.objectType !== 'media') return;
    setBusy('save');
    setError(null);
    try {
      const updated = await api.patch<MediaItem>(`/media/${selected.sourceId}`, { origin });
      setDetail(updated);
      setItems((prev) =>
        prev.map((item) => (item.key === selected.key ? { ...item, origin: updated.origin } : item)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function deleteSelected() {
    if (!selected || selected.objectType !== 'media') return;
    if (
      !window.confirm(
        `Delete "${displayTitle(selected)}" permanently? This removes it from storage and the database.`,
      )
    ) {
      return;
    }
    setBusy('delete');
    setError(null);
    setDeleteBlocked(null);
    try {
      await api.delete(`/media/${selected.sourceId}`);
      setSelectedId(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const data = err.data as { ideas?: string[] } | undefined;
        setDeleteBlocked({ ideas: data?.ideas ?? [] });
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  async function forceDeleteSelected() {
    if (!selected || selected.objectType !== 'media') return;
    setBusy('delete');
    setError(null);
    try {
      await api.delete(`/media/${selected.sourceId}?force=true`);
      setSelectedId(null);
      setDeleteBlocked(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function retry() {
    if (!selected || selected.objectType !== 'media') return;
    setBusy('retry');
    setError(null);
    try {
      await api.post(`/media/${selected.sourceId}/retry`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function searchLibrary(mode: LibrarySearchMode) {
    const query = searchQuery.trim();
    if (!query) {
      setSearchError('Enter a concept or question.');
      return;
    }
    setSearchBusy(mode);
    setSearchError(null);
    try {
      const result = await api.post<LibrarySearchResult>('/search', { query, mode });
      setSearchResult(result);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearchBusy(null);
    }
  }

  function openSearchMedia(id: number) {
    setPendingSearchMediaId(id);
    if (tab) {
      setLoading(true);
      setTab('');
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={onFilePicked}
      />

      {dragActive ? (
        <div className="dropzone-overlay">
          <span>Drop to add to library</span>
        </div>
      ) : null}

      {uploads.length > 0 ? (
        <div className="upload-panel">
          <div className="toolbar" style={{ marginBottom: 8 }}>
            <span className="label">Uploading</span>
            <span style={{ flex: '1 1 auto' }} />
            <button onClick={() => setUploads([])}>Dismiss</button>
          </div>
          {uploads.map((u, i) => (
            <div className="upload-row" key={`${u.name}-${i}`}>
              <span className="name">{u.name}</span>
              <span className={u.status === 'error' ? 'fail mono' : 'mono faint'}>
                {u.status === 'uploading' ? 'uploading…' : u.status === 'done' ? 'done' : (u.error ?? 'failed')}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <PageHead
        title="Library"
        subtitle="The source archive: conversations, documents, media, and published work in one searchable place."
        actions={
          <button className="primary" onClick={() => fileInputRef.current?.click()}>
            Add files
          </button>
        }
      />

      <form
        className="library-search"
        onSubmit={(event) => {
          event.preventDefault();
          void searchLibrary('search');
        }}
      >
        <input
          aria-label="Search or ask the library"
          placeholder="Search the library or ask a question"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <button className="primary" disabled={searchBusy !== null} type="submit">
          {searchBusy === 'search' ? 'Searching…' : 'Search'}
        </button>
        <button disabled={searchBusy !== null} type="button" onClick={() => void searchLibrary('ask')}>
          {searchBusy === 'ask' ? 'Asking…' : 'Ask'}
        </button>
      </form>

      {searchError ? <p className="fail">{searchError}</p> : null}
      {searchResult ? (
        <section className="library-search-results" aria-label="Library search results">
          <div className="toolbar" style={{ marginBottom: 12 }}>
            <span className="label">
              {searchResult.mode === 'ask' ? 'Answer and sources' : 'Search results'} · {searchResult.items.length}
            </span>
            <span style={{ flex: '1 1 auto' }} />
            <button type="button" onClick={() => setSearchResult(null)}>Clear</button>
          </div>
          {searchResult.answer ? <p className="library-search-answer">{searchResult.answer}</p> : null}
          {searchResult.items.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No matching library excerpts.</p>
          ) : (
            <div className="library-search-hit-list">
              {searchResult.items.map((hit, index) => {
                const isMedia = hit.sourceType !== 'message' && hit.sourceType !== 'publication';
                const content = (
                  <>
                    <span className="library-search-hit-meta">
                      [{index + 1}] {searchHitLabel(hit)}
                      {hit.author ? ` · ${hit.author}` : ''}
                      {hit.sentAt ? ` · ${formatDateTime(hit.sentAt)}` : ''}
                      {' · '}{Math.round(hit.score * 100)}%
                    </span>
                    <span className="library-search-hit-text">{hit.text}</span>
                  </>
                );
                return isMedia ? (
                  <button
                    className="library-search-hit"
                    key={`${hit.sourceType}-${hit.sourceId}-${index}`}
                    type="button"
                    onClick={() => openSearchMedia(hit.sourceId)}
                    title="Open this media item below"
                  >
                    {content}
                  </button>
                ) : (
                  <div className="library-search-hit" key={`${hit.sourceType}-${hit.sourceId}-${index}`}>
                    {content}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      <div className="library-filterbar">
        <div className="filter-tabs" aria-label="Library type">
          {TABS.map((t) => (
            <button
              key={t.label}
              aria-pressed={tab === t.kind}
              onClick={() => setTab(t.kind)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span>{loading ? 'Loading…' : error ? 'Unavailable' : `${items.length} loaded`}</span>
      </div>

      {previewIssue === 'storage_quota' ? (
        <div className="editorial-alert library-preview-alert" role="status">
          <div className="toolbar" style={{ marginBottom: 0 }}>
            <span><strong>Image previews unavailable.</strong> Media storage has reached its transfer limit; stored previews remain intact.</span>
            <span style={{ flex: '1 1 auto' }} />
            <button type="button" onClick={retryPreviews}>Retry previews</button>
          </div>
        </div>
      ) : null}

      {error ? <p className="fail">{error}</p> : null}

      {loading ? (
        <div className="empty">Loading…</div>
      ) : error ? null : items.length === 0 ? (
        <div className="empty">Nothing has been ingested for this view yet.</div>
      ) : (
        <div className="cards library-grid">
          {items.map((item) => {
            const preview = cardPreviews[item.key];
            const imageUrl = preview?.status === 'done' ? preview.url : undefined;
            const canShowFallback = preview?.status === 'missing' || preview?.status === 'failed';
            const bodyPreview = canShowFallback ? item.body?.trim() || null : null;
            const previewLabel = preview?.status === 'failed'
              ? 'Preview unavailable'
              : preview?.status === 'missing'
                ? item.kind.replaceAll('_', ' ')
                : 'Loading preview';
            return (
            <Fragment key={item.key}>
              <button
                type="button"
                className={`card media-card${selectedId === item.key ? ' active' : ''}`}
                onClick={() => setSelectedId(selectedId === item.key ? null : item.key)}
                aria-expanded={selectedId === item.key}
                aria-controls={`library-detail-${item.key}`}
              >
                <div className={`media-thumb-wrap${imageUrl ? ' has-image' : bodyPreview ? ' has-text' : ' compact'}`}>
                  {imageUrl ? (
                    <img
                      className="media-thumb"
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={() => setCardPreviews((previous) => ({
                        ...previous,
                        [item.key]: { status: 'failed' },
                      }))}
                    />
                  ) : bodyPreview ? (
                    <p className="media-text-preview">{bodyPreview}</p>
                  ) : (
                    <span className="media-glyph"><strong>{KIND_GLYPH[item.kind]}</strong><small>{previewLabel}</small></span>
                  )}
                </div>
                <div className="card-head">
                  <span className="library-card-title">
                    {displayTitle(item)}
                  </span>
                </div>
                <div className="library-card-meta">
                  <span className="chip">{item.kind}</span>
                  <PipelineStatus item={item} />
                  <time>{formatDateTime(item.createdAt)}</time>
                </div>
              </button>

              {selectedId === item.key ? (
                <div className="media-detail-panel" id={`library-detail-${item.key}`} ref={panelRef}>
                  <div className="toolbar" style={{ marginBottom: 18 }}>
                    <span className="label">
                      {item.objectType === 'publication' ? 'Publication' : 'Media'} #{item.sourceId}
                    </span>
                    <span style={{ flex: '1 1 auto' }} />
                    {item.objectType === 'media' && hasFailedStage(item) ? (
                      <button disabled={busy !== null} onClick={() => void retry()}>
                        {busy === 'retry' ? 'Retrying…' : 'Retry'}
                      </button>
                    ) : null}
                    {item.objectType === 'media' ? (
                      <button disabled={busy !== null} onClick={() => void deleteSelected()}>
                        {busy === 'delete' ? 'Deleting…' : 'Delete'}
                      </button>
                    ) : null}
                    <button onClick={() => setSelectedId(null)}>Close</button>
                  </div>

                  {deleteBlocked ? (
                    <div style={{ border: '1px solid var(--fail)', padding: 12, marginBottom: 20 }}>
                      <p className="fail" style={{ margin: '0 0 10px' }}>
                        Quoted by {deleteBlocked.ideas.length} idea{deleteBlocked.ideas.length === 1 ? '' : 's'}
                        {deleteBlocked.ideas.length > 0 ? `: ${deleteBlocked.ideas.join(', ')}` : ''} — deleting
                        would orphan their published provenance.
                      </p>
                      <div className="toolbar" style={{ marginBottom: 0 }}>
                        <button disabled={busy !== null} onClick={() => void forceDeleteSelected()}>
                          {busy === 'delete' ? 'Deleting…' : 'Delete anyway'}
                        </button>
                        <button onClick={() => setDeleteBlocked(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : null}

                  <div className="meta-grid" style={{ marginBottom: 20 }}>
                    <span className="label">Kind</span>
                    <span>
                      <span className="chip">{item.kind}</span>
                    </span>
                    <span className="label">Created</span>
                    <span className="mono faint">{formatDateTime(item.createdAt)}</span>
                    <span className="label">Updated</span>
                    <span className="mono faint">{formatDateTime(item.updatedAt)}</span>
                    {item.mime ? (
                      <>
                        <span className="label">MIME</span>
                        <span className="mono">{item.mime}</span>
                      </>
                    ) : null}
                    {item.sizeBytes !== null ? (
                      <>
                        <span className="label">Size</span>
                        <span className="mono">{bytes(item.sizeBytes)}</span>
                      </>
                    ) : null}
                    {item.durationSeconds !== null ? (
                      <>
                        <span className="label">Duration</span>
                        <span className="mono">{duration(item.durationSeconds)}</span>
                      </>
                    ) : null}
                    {item.sourceUrl ? (
                      <>
                        <span className="label">Source URL</span>
                        <span>
                          <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                            {item.sourceUrl}
                          </a>
                        </span>
                      </>
                    ) : null}
                    {item.canonicalUrl && item.canonicalUrl !== item.sourceUrl ? (
                      <>
                        <span className="label">Canonical URL</span>
                        <span>
                          <a href={item.canonicalUrl} target="_blank" rel="noreferrer">
                            {item.canonicalUrl}
                          </a>
                        </span>
                      </>
                    ) : null}
                    <span className="label">Origin</span>
                    <span>{originLabel(item.origin)}</span>
                    {item.contentHash ? (
                      <>
                        <span className="label">Content hash</span>
                        <span className="mono faint">{item.contentHash.slice(0, 16)}…</span>
                      </>
                    ) : null}
                    {item.reservedExcerptCount > 0 ? (
                      <>
                        <span className="label">Reserved</span>
                        <span>
                          {item.reservedExcerptCount} exact excerpt{item.reservedExcerptCount === 1 ? '' : 's'} in the queue
                        </span>
                      </>
                    ) : null}
                    {item.tgMessageId !== null ? (
                      <>
                        <span className="label">Source message</span>
                        <span className="mono faint">
                          chat {item.tgChatId} · message {item.tgMessageId}
                        </span>
                      </>
                    ) : null}
                    {item.pipeline && item.pipeline.attempts > 0 ? (
                      <>
                        <span className="label">Attempts</span>
                        <span className="mono">{item.pipeline.attempts}</span>
                      </>
                    ) : null}
                    {item.pipeline?.lastError ? (
                      <>
                        <span className="label">Last error</span>
                        <span className="fail">{item.pipeline.lastError}</span>
                      </>
                    ) : null}
                  </div>

                  {item.pipeline ? (
                    <>
                      <div className="label" style={{ marginBottom: 8 }}>Pipeline</div>
                      <div style={{ marginBottom: 20 }}><StageStrip item={item} /></div>
                    </>
                  ) : null}

                  {item.objectType === 'media' ? <div className="row" style={{ marginBottom: 18 }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <span className="label">Title</span>
                      <input
                        disabled={busy !== null}
                        value={draftTitle}
                        onChange={(e) => {
                          setDraftTitle(e.target.value);
                          queueAutoSave('title', e.target.value);
                        }}
                      />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <span className="label">Author</span>
                      <input
                        disabled={busy !== null}
                        value={draftAuthor}
                        onChange={(e) => {
                          setDraftAuthor(e.target.value);
                          queueAutoSave('author', e.target.value);
                        }}
                      />
                    </div>
                  </div> : null}
                  {item.objectType === 'media' ? (
                  <div className="field" style={{ marginBottom: 18 }}>
                    <span className="label">Origin classification</span>
                    <select
                      disabled={busy !== null || item.origin === 'agent_generated'}
                      value={item.origin}
                      onChange={(event) => void saveOrigin(event.target.value as LibraryOrigin)}
                    >
                      <option value="third_party">Third-party source</option>
                      <option value="collective_original">Collective original</option>
                      <option value="agent_generated">Agent generated</option>
                    </select>
                    {item.origin === 'agent_generated' ? (
                      <span className="hint">Searchable Library material. It cannot be used as publishing source text.</span>
                    ) : null}
                  </div>
                  ) : null}
                  {item.objectType === 'media' ? <div className="toolbar" style={{ marginBottom: 24 }}>
                    <span className="hint">
                      {busy === 'save'
                        ? 'Saving changes…'
                        : 'Attribution is identified during ingestion. Any correction saves automatically.'}
                    </span>
                  </div> : null}

                  <div className="label" style={{ marginBottom: 8 }}>
                    Extracted content
                  </div>
                  {item.objectType === 'publication' ? (
                    item.body ? <div className="article-body"><p>{item.body}</p></div> : <div className="empty">No published body stored.</div>
                  ) : !detail || detail.id !== item.sourceId ? (
                    <div className="empty">Loading…</div>
                  ) : detail.transcript || detail.aiDescription || detail.extractedText ? (
                    <div className="trace">
                      {CONTENT_FIELDS.map(({ label, field }) =>
                        detail[field] ? (
                          <details className="trace-event" key={field}>
                            <summary>
                              <span className="trace-type">{label}</span>
                            </summary>
                            <textarea
                              aria-label={label}
                              disabled={busy !== null}
                              rows={Math.max(6, Math.ceil(draftContent[field].length / 100))}
                              value={draftContent[field]}
                              onChange={(e) => {
                                setDraftContent((prev) => ({ ...prev, [field]: e.target.value }));
                                queueAutoSave(field, e.target.value);
                              }}
                            />
                          </details>
                        ) : null,
                      )}
                    </div>
                  ) : (
                    <div className="empty">No transcript, description, or extracted text yet.</div>
                  )}
                </div>
              ) : null}
            </Fragment>
            );
          })}
        </div>
      )}
    </>
  );
}
